import { RefObject, useCallback, useRef, useState } from "react";
import { useAboveFold } from "web/react/hooks/use-above-fold/use-above-fold";
import useIsomorphicLayoutEffect from "web/react/hooks/use-isomorphic-layout-effect/use-isomorphic-layout-effect";
import analytics from "web/script/analytics/analytics";

interface UseCuratedCollectionsAnalyticsProps {
    label: string;
    productId?: string;
    buyOptionId?: string;
    curatedCollectionSlug: string;
    isSponsored: boolean;
    position?: number;
    url?: string;
    visibleThreshold?: number;
    componentType?: string;
    gender: "womenswear" | "menswear" | "mixed";
}

interface UseCuratedCollectionsAnalyticsResults {
    containerRef: RefObject<HTMLDivElement>;
    sendClickEvent: (action: string) => void;
}

export function useCuratedCollectionsAnalytics(
    params: UseCuratedCollectionsAnalyticsProps
): UseCuratedCollectionsAnalyticsResults {
    const {
        label,
        productId,
        buyOptionId,
        curatedCollectionSlug,
        isSponsored,
        position,
        url,
        visibleThreshold,
        componentType,
        gender,
    } = params;

    const containerRef = useRef<HTMLDivElement>(null);
    const { isAboveFold, isVisible } = useAboveFold(containerRef, visibleThreshold || 0.5);
    const [hasImpression, setHasImpression] = useState(false);
    const sendAnalyticsEvent = useCallback(
        (action: string, area?: string) => {
            analytics.event(
                "curated_collections",
                action,
                label,
                action !== "clicked", // nonInteraction
                {}, // customData
                "", // subType
                {}, // checkoutData
                [
                    // itemData
                    {
                        above_fold: isAboveFold,
                        component_type: componentType || label,
                        curated_collection_slug: curatedCollectionSlug,
                        item_type: "curated_collections",
                        editorial_gender: gender,
                        ...(typeof isSponsored === "boolean" && { sponsored: isSponsored }),
                        ...(area && { area: area }),
                        ...(buyOptionId && { buy_option_id: buyOptionId }),
                        ...(typeof position === "number" && { position: position }),
                        ...(productId && { product_id: productId }),
                        ...(url && { url }),
                    },
                ],
                false, // lock
                false // truncate
            );
        },
        [
            buyOptionId,
            curatedCollectionSlug,
            isAboveFold,
            isSponsored,
            label,
            position,
            productId,
            url,
            componentType,
            gender,
        ]
    );

    function sendClickEvent(area: string): void {
        sendAnalyticsEvent("clicked", area);
    }

    useIsomorphicLayoutEffect(() => {
        if (isVisible && !hasImpression) {
            sendAnalyticsEvent("viewed");
            setHasImpression(true);
        }
    }, [isVisible, hasImpression, sendAnalyticsEvent, setHasImpression]);

    return { containerRef, sendClickEvent };
}
